import { v4 as uuidv4 } from "uuid";
import firebase from "firebase/compat/app";
import "firebase/compat/firestore";
import {
  getCreationStamp,
  getModificationStamp,
  getZooId,
  initAuth,
  initDb,
} from "../tools/conf";
import { FilesController } from "./filesController";
import { ImageController } from "./imageController.js";
import { getAnimalsQueryScope } from "../utils/firestore/animalsZooScope";
import { invalidatePrefix } from "../utils/cache/refDataCache";
import { getEnvironmentalTrackingFieldUpdates } from "../utils/environmental/environmentalTracking";

// Auteur d'un archivage : celui transmis par l'écran, à défaut le compte connecté.
// La fiche secteur Nouvelle (intégrée) ne transmettait pas d'utilisateur, d'où
// archivedBy null (recette 12.1.0, COL-08).
export const archiveActorId = (userId) => {
  if (userId) return userId;
  try {
    return (typeof initAuth === "function" ? initAuth()?.currentUser?.uid : null) || null;
  } catch (e) {
    return null;
  }
};

// L'index des secteurs est servi depuis le cache de référentiel (`sectors:<zoo>`,
// 60 s) par la fiche enclos V2 et sa gateway : toute mutation de secteur le vide.
const invalidateSectorIndexCache = () => invalidatePrefix("sectors:");

const cdn = new FilesController();

// Champs du document enclosures/{id} maintenus exclusivement par les cloud
// functions, jamais par le formulaire enclos : le set() complet de
// _modifyEnclosure doit refléter la valeur actuelle du doc, sinon chaque
// sauvegarde les efface (champ absent du payload) ou les réécrit stale
// (valeur du state chargée à l'ouverture du formulaire).
export const CLOUD_MAINTAINED_ENCLOSURE_FIELDS = [
  "objects", // IDs d'objets d'enrichissement legacy — functions/objects/object.js
  "enrichList", // enrichissements actifs par session — functions/sessions/sessions.js
  "currentVersion", // numéro de version courant — functions/enclosure/enclosureAnimalSync.js
];

// Estampille de création posée par _createEnclosure (cf. getCreationStamp) :
// immuable par définition. Le set() complet de _modifyEnclosure doit la relire
// dans le doc et la réinjecter, sinon la première édition de la fiche efface
// l'auteur de l'enclos — c'est exactement ce qui empêche aujourd'hui de savoir
// qui a créé les enclos existants.
export const IMMUTABLE_ENCLOSURE_CREATION_FIELDS = [
  "createdBy",
  "createdByName",
  "createdAt",
];

// Réglages de la fiche écrits par update ciblé depuis leur propre panneau : le
// formulaire n'a pas autorité dessus. Le set() complet de _modifyEnclosure les
// relit dans le doc, sinon enregistrer une fiche ouverte avant le réglage le
// remettrait à l'ancienne valeur (state chargé à l'ouverture) ou l'effacerait.
export const TARGETED_ENCLOSURE_SETTINGS_FIELDS = [
  "objectivesEnabled", // pilotage BEA, activé depuis la vue collection

  "environmentalTracking", // suivi environnemental enclos / zones — src/utils/environmental/environmentalTracking.js
];

// Supprime récursivement toute valeur `undefined` d'un payload avant un set()
// Firestore. UN SEUL champ `undefined` n'importe où (typiquement l'objet
// `reference` d'une image de hauteurs encore en cours d'upload) fait rejeter
// TOUT le document → la sauvegarde de la fiche enclos est perdue silencieusement.
// On ne descend que dans les objets "plain" et les tableaux ; Date / File / et
// autres instances sont laissés intacts.
export const stripUndefinedDeep = (value) => {
  if (Array.isArray(value)) {
    return value.map((v) => stripUndefinedDeep(v));
  }
  if (
    value !== null &&
    typeof value === "object" &&
    value.constructor === Object
  ) {
    const out = {};
    for (const key of Object.keys(value)) {
      if (value[key] === undefined) continue;
      out[key] = stripUndefinedDeep(value[key]);
    }
    return out;
  }
  return value;
};

export class SiteDatabaseConnector {
  async _getEnclosureFromName(enclosureName) {
    let db = initDb();
    let zooId = await getZooId();
    let list = [];
    let snapshot = await db
      .collection("enclosures")
      .where("zooId", "==", zooId)
      .where("enclosureName", "==", enclosureName)
      .get();
    snapshot.forEach((doc) => {
      list.push(doc.data());
    });
    return list;
  }

  async _getVisitsFromId(reportId) {
    let db = initDb();
    let data = await (await db.collection("visits").doc(reportId).get()).data();
    return data;
  }

  async _getEnclosureListFromSectorId(sectorId) {
    let db = initDb();
    let zooId = await getZooId();
    let list = [];
    let snapshot = await db
      .collection("enclosures")
      .where("zooId", "==", zooId)
      .where("linkedSectorId", "==", sectorId)
      .get();
    snapshot.forEach((doc) => {
      let data = doc.data();
      if (!data.isArchived) {
        list.push(data);
      }
    });
    return list;
  }

  async uploadAreaPhotoAndPopulateUrl(enclosureData) {
    let populatedArea = [];
    await Promise.all(
      enclosureData.areas.map(async (area) => {
        if (area.shouldUpload) {
          const timestampUnix = Date.now();
          const filename = `${timestampUnix}.jpg`;
          let file = await cdn.urltoFile(
            area.areaImage,
            filename,
            "image/jpg"
          );
          let url = null;
          let storageRef = await cdn._handleUploadFile(
            null,
            file,
            null,
            `${enclosureData.zooId}/enclosures/${enclosureData.enclosureId}/areas/${area.areaId}/`,
            enclosureData.enclosureId,
            () => {},
            (e) => (url = e),
            () => {},
            undefined,
            filename
          );
          delete area.shouldUpload;
          populatedArea.push({
            ...area,
            areaImage: storageRef,
            areaImageUploadedAt: timestampUnix,
          });
        } else {
          populatedArea.push(area);
        }
      })
    );
    enclosureData.areas = populatedArea;
    return enclosureData;
  }

  // Upload a historical area image (used by the area-version history editor).
  // Stores at {zooId}/enclosures/{enclosureId}/areas/{areaId}/historical_{timestampUnix}.jpg
  // and returns the storage URL.
  async uploadHistoricalAreaImage(
    zooId,
    enclosureId,
    areaId,
    fileOrDataUrl,
    timestampUnix
  ) {
    const filename = `historical_${timestampUnix}.jpg`;
    const folder = `${zooId}/enclosures/${enclosureId}/areas/${areaId}/`;
    let file = fileOrDataUrl;
    if (typeof fileOrDataUrl === "string") {
      file = await cdn.urltoFile(fileOrDataUrl, filename, "image/jpg");
    }
    const url = await cdn._handleUploadFile(
      null,
      file,
      null,
      folder,
      enclosureId,
      () => {},
      () => {},
      () => {},
      undefined,
      filename
    );
    return url;
  }

  async uploadProfilePhotoAndPopulateUrl(enclosureData) {
    console.log(enclosureData.enclosureProfilePicture);

    if (enclosureData?.enclosureProfilePicture?.shouldUpload) {
      let url = null;

      let file = await cdn.urltoFile(
        enclosureData.enclosureProfilePicture.uri,
        `${enclosureData.enclosureId}.jpg`,
        "image/jpg"
      );

      let storageRef = await cdn._handleUploadFile(
        null,
        file,
        null,
        `${enclosureData.zooId}/enclosures/${enclosureData.enclosureId}/`,
        enclosureData.enclosureData,
        () => {},
        (e) => (url = e),
        () => {},
        undefined,
        `${enclosureData.enclosureId}.jpg`
      );

      enclosureData.enclosureProfilePicture.url = storageRef;

      delete enclosureData.enclosureProfilePicture.uri;
      delete enclosureData.enclosureProfilePicture.shouldUpload;
    }

    return enclosureData;
  }

  async uploadSectorProfilePhotoAndPopulateUrl(sectorData) {
    console.log("[uploadSectorProfilePhoto]", sectorData.sectorProfilePicture);

    if (sectorData?.sectorProfilePicture?.shouldUpload) {
      let url = null;

      let file = await cdn.urltoFile(
        sectorData.sectorProfilePicture.uri,
        `${sectorData.sectorId}.jpg`,
        "image/jpg"
      );

      let storageRef = await cdn._handleUploadFile(
        null,
        file,
        null,
        `${sectorData.zooId}/sectors/${sectorData.sectorId}/`,
        sectorData.sectorData,
        () => {},
        (e) => (url = e),
        () => {},
        undefined,
        `${sectorData.sectorId}.jpg`
      );

      sectorData.sectorProfilePicture = { url: storageRef };
    }

    return sectorData;
  }

  async _createEnclosure(enclosureData) {
    let db = initDb();
    let zooId = await getZooId();
    let uuid = uuidv4();

    let newEntry = {
      zooId: zooId,
      enclosureId: uuid,
      label: enclosureData.enclosureName,
      value: uuid,
      ...enclosureData,
    };

    // upload Photo and populate url

    newEntry = await this.uploadAreaPhotoAndPopulateUrl(newEntry);
    newEntry = await this.uploadProfilePhotoAndPopulateUrl(newEntry);

    // Filet de sécurité : retirer tout `undefined` avant le set() (cf. _modifyEnclosure).
    newEntry = stripUndefinedDeep(newEntry);

    // Après le spread d'`enclosureData` : une duplication de fiche ne doit pas
    // recopier l'auteur de l'enclos d'origine.
    newEntry = { ...newEntry, ...getCreationStamp(), ...getModificationStamp() };

    try {
      await db.collection("enclosures").doc(uuid).set(newEntry);
      return { success: true, data: newEntry };
    } catch (error) {
      console.error(error);
      return { success: false, data: error };
    }
  }

  async _modifyEnclosure(enclosureData) {
    let db = initDb();
    let newEntry = {
      ...enclosureData,
      label: enclosureData.enclosureName,
    };

    newEntry = await this.uploadAreaPhotoAndPopulateUrl(newEntry);
    newEntry = await this.uploadProfilePhotoAndPopulateUrl(newEntry);

    // Filet de sécurité : on retire tout `undefined` AVANT le set() pour qu'un
    // champ partiel (ex. image de référence des hauteurs en cours d'upload) ne
    // fasse pas rejeter l'intégralité du document par Firestore.
    newEntry = stripUndefinedDeep(newEntry);

    newEntry = { ...newEntry, ...getModificationStamp() };

    // DEBUG: Log fields with undefined values before Firestore set()
    const findUndefinedFields = (obj, path = '') => {
      const undefinedFields = [];
      for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
          const currentPath = path ? `${path}.${key}` : key;
          if (obj[key] === undefined) {
            undefinedFields.push(currentPath);
          } else if (obj[key] !== null && typeof obj[key] === 'object' && !Array.isArray(obj[key])) {
            undefinedFields.push(...findUndefinedFields(obj[key], currentPath));
          } else if (Array.isArray(obj[key])) {
            obj[key].forEach((item, index) => {
              if (item === undefined) {
                undefinedFields.push(`${currentPath}[${index}]`);
              } else if (item !== null && typeof item === 'object') {
                undefinedFields.push(...findUndefinedFields(item, `${currentPath}[${index}]`));
              }
            });
          }
        }
      }
      return undefinedFields;
    };

    const undefinedFields = findUndefinedFields(newEntry);
    if (undefinedFields.length > 0) {
      console.error('🚨 [_modifyEnclosure] Found undefined fields:', undefinedFields);
      console.error('🚨 [_modifyEnclosure] Full data object:', JSON.stringify(newEntry, (key, value) => value === undefined ? '__UNDEFINED__' : value, 2));
    } else {
      console.log('✅ [_modifyEnclosure] No undefined fields found');
    }

    try {
      const docRef = db
        .collection("enclosures")
        .doc(enclosureData.enclosureId);

      // set() sans merge : tout champ absent du payload est effacé du doc.
      // On relit dans une transaction les champs que le formulaire n'a pas
      // autorité pour écrire — ceux maintenus par les cloud functions (résiste
      // à un déplacement d'objet survenu pendant que le formulaire était
      // ouvert) et l'estampille de création (immuable). Champ absent du doc
      // (enclos créés avant l'ajout de l'estampille) → on le supprime du
      // payload plutôt que d'inventer un auteur depuis le state du formulaire.
      await db.runTransaction(async (transaction) => {
        const snapshot = await transaction.get(docRef);
        const currentData = snapshot.exists ? snapshot.data() : {};
        [
          ...CLOUD_MAINTAINED_ENCLOSURE_FIELDS,
          ...IMMUTABLE_ENCLOSURE_CREATION_FIELDS,
          ...TARGETED_ENCLOSURE_SETTINGS_FIELDS,
        ].forEach((field) => {
          if (currentData[field] !== undefined) {
            newEntry[field] = currentData[field];
          } else {
            delete newEntry[field];
          }
        });
        transaction.set(docRef, newEntry);
      });
      return { success: true, data: newEntry };
    } catch (error) {
      console.error(error);
      return { success: false, data: error };
    }
  }

  async _getEnclosuresList(requestedZooId) {
    let db = initDb();
    let zooId = requestedZooId || await getZooId();
    let list = [];

    try {
      let snapshot = await db
        .collection("enclosures")
        .where("zooId", "==", zooId)
        .get();
      if (snapshot.empty) {
      } else {
        snapshot.forEach((doc) => {
          list.push(doc.data());
        });
      }

      list = list.filter((el) => !el.isArchived);

      // Enrichir avec le nom du secteur : une lecture par secteur, pas une par enclos (les enclos
      // d'un parc partagent souvent le même secteur ; retour du 13/10, fiche individu trop lente).
      const sectorReads = new Map();
      const readSector = (sectorId) => {
        if (!sectorReads.has(sectorId)) sectorReads.set(sectorId, this._getSectorFromId(sectorId));
        return sectorReads.get(sectorId);
      };
      const enrichedList = await Promise.all(
        list.map(async (enclosure) => {
          if (enclosure.linkedSectorId) {
            try {
              const sectorData = await readSector(enclosure.linkedSectorId);
              return {
                ...enclosure,
                sectorName: sectorData?.sectorName,
                sector: sectorData,
              };
            } catch (error) {
              console.error(
                `Error fetching sector for enclosure ${enclosure.enclosureId}:`,
                error
              );
              return enclosure;
            }
          }
          return enclosure;
        })
      );

      return enrichedList;
    } catch (error) {
      console.log(error);
      return {
        success: false,
        data: error,
      };
    }
  }

  async _getEnclosureDetails(enclosureId) {
    let db = initDb();
    const { zooId } = await getAnimalsQueryScope();
    let snapshot = await db
      .collection("animals")
      .where("zooId", "==", zooId)
      .where("animalEnclosureData.enclosureId", "==", enclosureId)
      .get();
    let animalList = [];
    snapshot.forEach((doc) => {
      let data = doc.data();
      if (data.isArchived === false) {
        animalList.push(data);
      }
    });

    let specieList = [];

    animalList.map((animal) => {
      let index = specieList.findIndex(
        (item) => item.specieId === animal.specieId
      );
      if (index === -1) {
        specieList.push({
          specieId: animal.specieId,
          specieName: animal.specieName,
          specieLatinName: animal.specieLatinName,
          specieEnglishName: animal.specieEnglishName,
        });
      }
      return null;
    });

    return {
      speciesList: specieList,
      animalsData: animalList,
    };
  }

  /**
   * Occupants de PLUSIEURS enclos : une requête par lot de 30 identifiants (`in`), au lieu d'une
   * requête par enclos. Rend `{ [enclosureId]: { speciesList, animalsData } }`, chaque valeur ayant
   * exactement la forme de `_getEnclosureDetails` ; un enclos sans occupant rend des listes vides.
   *
   * Les listes (secteurs, tableau de bord, fiches secteur) appelaient `_getEnclosureDetails` en
   * boucle : une liste de 300 enclos coûtait 300 requêtes pour afficher des compteurs.
   * Même périmètre de parc que la requête unitaire (rules `animals` : « rules are not filters »).
   */
  async _getEnclosuresDetails(enclosureIds) {
    const ids = [...new Set((enclosureIds || []).filter(Boolean))];
    const details = Object.fromEntries(ids.map((id) => [id, { speciesList: [], animalsData: [] }]));
    if (ids.length === 0) return details;
    const db = initDb();
    const { zooId } = await getAnimalsQueryScope();
    const chunks = [];
    for (let i = 0; i < ids.length; i += 30) chunks.push(ids.slice(i, i + 30));
    const snapshots = await Promise.all(chunks.map((chunk) => db
      .collection("animals")
      .where("zooId", "==", zooId)
      .where("animalEnclosureData.enclosureId", "in", chunk)
      .get()));
    snapshots.forEach((snapshot) => snapshot.forEach((doc) => {
      const data = doc.data();
      const entry = details[data.animalEnclosureData?.enclosureId];
      if (!entry || data.isArchived !== false) return;
      entry.animalsData.push(data);
      if (!entry.speciesList.some((item) => item.specieId === data.specieId)) {
        entry.speciesList.push({
          specieId: data.specieId,
          specieName: data.specieName,
          specieLatinName: data.specieLatinName,
          specieEnglishName: data.specieEnglishName,
        });
      }
    }));
    return details;
  }

  async _getEnclosureDataFromIdWithoutPP(enclosureId) {
    let db = initDb();
    let data;
    try {
      let snapshot = await db.collection("enclosures").doc(enclosureId).get();

      data = snapshot.data();

      return data;
    } catch (error) {
      return data;
    }
  }

  async _getEnclosureDataFromId(enclosureId, noPicture) {
    let db = initDb();
    let ImageControl = new ImageController();
    let data;
    try {
      let snapshot = await db.collection("enclosures").doc(enclosureId).get();

      data = snapshot.data();

      data.areas.map(async (area) => {
        try {
          if (noPicture) {
            area.areaImageUrl = "";
          } else {
            let url = await ImageControl.getUrlFromStorageRef(area.areaImage);
            area.areaImageUrl = url;
          }
        } catch (error) {}
      });
      return data;
    } catch (error) {
      return data;
    }
  }

  async _updateEnclosure(enclosureData) {
    let db = initDb();
    enclosureData = {
      ...enclosureData,
      label: enclosureData.enclosureName,
      value: enclosureData.enclosureId,
      ...getModificationStamp(),
    };
    try {
      db.collection("enclosures")
        .doc(enclosureData.enclosureId)
        .update(enclosureData);
      return { success: true, data: enclosureData };
    } catch (error) {
      return { success: false, data: error };
    }
  }

  async _getAnimalsInGroup(groupId) {
    let db = initDb();
    let list = [];
    let snapshot = await db
      .collection("groups")
      .doc(groupId)
      .collection("animals")
      .get();
    snapshot.forEach((doc) => {
      list.push(doc.data());
    });
    return list;
  }

  async _getGroupList() {
    let db = initDb();
    let zooId = await getZooId();
    let list = [];
    let snapshot = await db
      .collection("groups")
      .where("zooId", "==", zooId)
      .get();
    snapshot.forEach((doc) => {
      list.push(doc.data());
    });
    return list;
  }

  async _getGroupFromId(groupId) {
    let db = initDb();
    let data = await (await db.collection("groups").doc(groupId).get()).data();
    return data;
  }

  /**
   * Get all fast groups (from fastGroups collection) for the current zoo
   * This is the new group structure used by FastEval/GroupListView
   * @returns {Promise<Array>} List of fast groups with id, name, etc.
   */
  async _getFastGroupsList(requestedZooId) {
    let db = initDb();
    let zooId = requestedZooId || await getZooId();
    let list = [];
    let snapshot = await db
      .collection("fastGroups")
      .where("zooId", "==", zooId)
      .get();
    snapshot.forEach((doc) => {
      const data = doc.data();
      // Skip archived groups (filter client-side to handle undefined/null/false)
      if (data.isArchived === true) {
        return;
      }
      // Skip soft-deleted groups (filter client-side to handle undefined/null/false)
      if (data.isDeleted === true) {
        return;
      }
      list.push({
        groupId: doc.id,
        groupName: data.name || data.groupName,
        specieName: data.specieName,
        sectorId: data.sectorId,
        individualCount: data.individualCount,
        ...data,
      });
    });
    return list;
  }

  async _getGroupFromAnimalId() {
    let db = initDb();
    let zooId = await getZooId();
    let list = [];
    let snapshot = await db
      .collection("groups")
      .where("zooId", "==", zooId)
      .get();
    snapshot.forEach((doc) => {
      list.push(doc.data());
    });
    return list;
  }

  async _updateAnimalInGroup(siteId, groupId, newList) {
    let db = await initDb();
    let zooId = await getZooId();
    try {
      db.ref(zooId + "/groupsList/" + groupId).update({ animalList: newList });
      return { success: true, data: newList };
    } catch (error) {
      return { success: false, data: error };
    }
  }

  async _setNewGroup(groupData) {
    let db = await initDb();
    let zooId = await getZooId();

    let newEntry = {
      ...groupData,
      label: groupData.groupName,
      value: groupData.groupId,
      zooId: zooId,
      ...getCreationStamp(),
      ...getModificationStamp(),
    };

    try {
      await db.collection("groups").doc(newEntry.groupId).set(newEntry);
      return { success: true, data: newEntry };
    } catch (error) {
      console.log(error);
      return { success: false, data: error };
    }
  }

  async _updateGroup(groupData) {
    let db = await initDb();

    try {
      groupData = { ...groupData, ...getModificationStamp() };
      await db.collection("groups").doc(groupData.groupId).update(groupData);
      return { success: true, data: groupData };
    } catch (error) {
      console.log(error);
      return { success: false, data: error };
    }
  }

  async _removeGroup(groupId) {
    try {
      let db = await initDb();
      await db.collection("groups").doc(groupId).delete();
      return { success: true, data: null };
    } catch (error) {
      return { success: false, data: error };
    }
  }
  async _removeEnclosure(enclosureId) {
    try {
      let db = await initDb();
      await db.collection("enclosures").doc(enclosureId).delete();
      return { success: true, data: null };
    } catch (error) {
      return { success: false, data: error };
    }
  }

  async _getZooData(zooId) {
    try {
      let db = await initDb();
      const zooDoc = await db.collection("zoo").doc(zooId).get();
      if (zooDoc.exists) {
        return { success: true, data: zooDoc.data() };
      } else {
        return { success: false, data: "Zoo not found" };
      }
    } catch (error) {
      return { success: false, data: error };
    }
  }

  async _updateSector(sectorData) {
    invalidateSectorIndexCache();
    let db = initDb();
    let zooId = await getZooId();
    console.log(
      "[siteDatabaseConnector] _updateSector called with:",
      sectorData
    );

    // Add zooId to sectorData for upload function
    sectorData.zooId = zooId;

    // Upload profile photo if needed
    sectorData = await this.uploadSectorProfilePhotoAndPopulateUrl(sectorData);

    let newEntry = {
      sectorName: sectorData.sectorName,
      label: sectorData.sectorName,
      value: sectorData.sectorId,
      sectorKeeper: sectorData.sectorKeeper || [],
      // Ponctuels et couleur : écrits seulement s'ils sont fournis, sinon la
      // sauvegarde des rôles de support les remettait à [] / #4e73df (COL-03).
      ...(sectorData.punctualKeeper !== undefined && { punctualKeeper: sectorData.punctualKeeper || [] }),
      // Only persist role arrays that are explicitly provided, so partial
      // updates (e.g. keeper enrollment from user settings) don't wipe the
      // existing role assignments stored on the sector.
      ...(sectorData.managementUserIds !== undefined && { managementUserIds: sectorData.managementUserIds }),
      ...(sectorData.veterinaryUserIds !== undefined && { veterinaryUserIds: sectorData.veterinaryUserIds }),
      ...(sectorData.nutritionUserIds !== undefined && { nutritionUserIds: sectorData.nutritionUserIds }),
      ...(sectorData.technicalUserIds !== undefined && { technicalUserIds: sectorData.technicalUserIds }),
      ...(sectorData.zoologicalUserIds !== undefined && { zoologicalUserIds: sectorData.zoologicalUserIds }),
      // Référents training (GitLab #1104)
      ...(sectorData.trainingUserIds !== undefined && { trainingUserIds: sectorData.trainingUserIds }),
      // Include sector color
      ...(sectorData.sectorColor !== undefined && { sectorColor: sectorData.sectorColor || "#4e73df" }),
      // Include profile picture if provided
      ...(sectorData.sectorProfilePicture && {
        sectorProfilePicture: sectorData.sectorProfilePicture,
      }),
    };

    console.log("[siteDatabaseConnector] Updating with newEntry:", newEntry);

    newEntry = { ...newEntry, ...getModificationStamp() };

    try {
      await db.collection("sectors").doc(sectorData.sectorId).update(newEntry);
      console.log("[siteDatabaseConnector] Update successful");
      return { success: true, data: newEntry };
    } catch (error) {
      return { success: false, data: error };
    }
  }

  // Affectation d'un soigneur à un secteur depuis la fiche utilisateur :
  // écriture ciblée de sectorKeeper seulement (arrayUnion / arrayRemove).
  // _updateSector réécrit couleur et ponctuels (défaut #4e73df, []) et
  // repartirait d'un index éventuellement périmé (recette 12.1.0, COL-03).
  async _setSectorKeeperMembership(sectorId, userId, isKeeper) {
    invalidateSectorIndexCache();
    if (!sectorId || !userId) return { success: false, data: "MISSING_ID" };
    const db = initDb();
    const { FieldValue } = firebase.firestore;
    try {
      await db.collection("sectors").doc(sectorId).update({
        sectorKeeper: isKeeper ? FieldValue.arrayUnion(userId) : FieldValue.arrayRemove(userId),
        ...getModificationStamp(),
      });
      return { success: true, data: null };
    } catch (error) {
      return { success: false, data: error };
    }
  }

  async _createSector(sectorData) {
    invalidateSectorIndexCache();
    let db = initDb();
    let zooId = await getZooId();
    let uuid = uuidv4();

    let newEntry = {
      zooId: zooId,
      sectorId: uuid,
      label: sectorData.sectorName,
      value: uuid,
      ...sectorData,
      // Initialize keeper arrays for new sectors
      sectorKeeper: sectorData.sectorKeeper || [],
      punctualKeeper: sectorData.punctualKeeper || [],
      // Initialize role arrays for new sectors
      managementUserIds: sectorData.managementUserIds || [],
      veterinaryUserIds: sectorData.veterinaryUserIds || [],
      nutritionUserIds: sectorData.nutritionUserIds || [],
      technicalUserIds: sectorData.technicalUserIds || [],
      zoologicalUserIds: sectorData.zoologicalUserIds || [],
      trainingUserIds: sectorData.trainingUserIds || [],
      // Include sector color
      sectorColor: sectorData.sectorColor || "#4e73df",
      // Initialize archive state
      isArchived: false,
    };

    // Upload profile photo if needed
    newEntry = await this.uploadSectorProfilePhotoAndPopulateUrl(newEntry);

    newEntry = { ...newEntry, ...getCreationStamp(), ...getModificationStamp() };

    try {
      await db.collection("sectors").doc(uuid).set(newEntry);
      return { success: true, data: newEntry };
    } catch (error) {
      console.error(error);
      return { success: false, data: error };
    }
  }

  async _canArchiveSector(sectorId) {
    let db = initDb();
    let zooId = await getZooId();
    let snapshot = await db
      .collection("enclosures")
      .where("zooId", "==", zooId)
      .where("linkedSectorId", "==", sectorId)
      .get();
    let activeEnclosures = [];
    snapshot.forEach((doc) => {
      let data = doc.data();
      if (!data.isArchived) {
        activeEnclosures.push(data);
      }
    });
    return {
      canArchive: activeEnclosures.length === 0,
      activeEnclosuresCount: activeEnclosures.length,
    };
  }

  async _archiveSector(sectorId, userId) {
    invalidateSectorIndexCache();
    let db = initDb();
    console.log(
      "[siteDatabaseConnector] _archiveSector called with sectorId:",
      sectorId
    );

    try {
      await db.collection("sectors").doc(sectorId).update({
        isArchived: true,
        archivedAt: new Date(),
        archivedBy: archiveActorId(userId),
      });
      console.log("[siteDatabaseConnector] Archive successful");
      return { success: true };
    } catch (error) {
      console.error("[siteDatabaseConnector] Archive failed:", error);
      return { success: false, error: error };
    }
  }

  async _unarchiveSector(sectorId) {
    invalidateSectorIndexCache();
    let db = initDb();
    console.log(
      "[siteDatabaseConnector] _unarchiveSector called with sectorId:",
      sectorId
    );

    try {
      await db.collection("sectors").doc(sectorId).update({
        isArchived: false,
        archivedAt: null,
        archivedBy: null,
      });
      console.log("[siteDatabaseConnector] Unarchive successful");
      return { success: true };
    } catch (error) {
      console.error("[siteDatabaseConnector] Unarchive failed:", error);
      return { success: false, error: error };
    }
  }

  async _canArchiveEnclosure(enclosureId) {
    let db = initDb();
    const { zooId } = await getAnimalsQueryScope();
    let snapshot = await db
      .collection("animals")
      .where("zooId", "==", zooId)
      .where("animalEnclosureData.enclosureId", "==", enclosureId)
      .get();
    let activeAnimals = [];
    snapshot.forEach((doc) => {
      let data = doc.data();
      // Un animal est actif s'il n'est ni décédé, ni parti, ni archivé
      if (!data.isArchived && !data.isDeceased && !data.isDeparted) {
        activeAnimals.push(data);
      }
    });
    return {
      canArchive: activeAnimals.length === 0,
      activeAnimalsCount: activeAnimals.length,
    };
  }

  async _archiveEnclosure(enclosureId, userId) {
    let db = initDb();
    console.log(
      "[siteDatabaseConnector] _archiveEnclosure called with enclosureId:",
      enclosureId
    );

    try {
      await db.collection("enclosures").doc(enclosureId).update({
        isArchived: true,
        archivedAt: new Date(),
        archivedBy: archiveActorId(userId),
      });
      console.log("[siteDatabaseConnector] Enclosure archive successful");
      return { success: true };
    } catch (error) {
      console.error("[siteDatabaseConnector] Enclosure archive failed:", error);
      return { success: false, error: error };
    }
  }

  async _unarchiveEnclosure(enclosureId) {
    let db = initDb();
    console.log(
      "[siteDatabaseConnector] _unarchiveEnclosure called with enclosureId:",
      enclosureId
    );

    try {
      await db.collection("enclosures").doc(enclosureId).update({
        isArchived: false,
        archivedAt: null,
        archivedBy: null,
      });
      console.log("[siteDatabaseConnector] Enclosure unarchive successful");
      return { success: true };
    } catch (error) {
      console.error("[siteDatabaseConnector] Enclosure unarchive failed:", error);
      return { success: false, error: error };
    }
  }

  // Lien enclos AWM ↔ enclos ZIMS (collection ZIMS). Update ciblé : ne touche
  // à rien d'autre du doc (pas de version++, champs cloud intacts).
  // zimsEnclosure: { guid, name } ou null pour délier.
  async _setEnclosureZimsLink(enclosureId, zimsEnclosure) {
    let db = initDb();
    try {
      await db.collection("enclosures").doc(enclosureId).update({
        zimsEnclosure: zimsEnclosure || null,
      });
      return { success: true };
    } catch (error) {
      console.error("[siteDatabaseConnector] _setEnclosureZimsLink failed:", error);
      return { success: false, error: error };
    }
  }

  // Suivi environnemental de l'enclos et de ses zones : seul le réglage modifié
  // ({ enabled }, { habitat } ou { zones: { [areaId]: { enabled } } }) est écrit,
  // par chemin de champ. Réécrire l'objet entier depuis l'état d'un écran ouvert
  // plus tôt annulait les réglages faits sur un autre écran (cf.
  // src/utils/environmental/environmentalTracking.js). FieldPath : un areaId
  // n'est jamais relu comme un chemin pointé.
  async _setEnclosureEnvironmentalTracking(enclosureId, change) {
    let db = initDb();
    const { FieldPath } = firebase.firestore;
    const fieldsAndValues = getEnvironmentalTrackingFieldUpdates(change).flatMap(([segments, value]) => [
      new FieldPath("environmentalTracking", ...segments),
      value,
    ]);
    if (fieldsAndValues.length === 0) return { success: true };
    try {
      await db.collection("enclosures").doc(enclosureId).update(...fieldsAndValues);
      return { success: true };
    } catch (error) {
      console.error("[siteDatabaseConnector] _setEnclosureEnvironmentalTracking failed:", error);
      return { success: false, error: error };
    }
  }

  // Type d'une zone ('enclosure' = Terrestre, 'basin' = Bassin) réglé depuis le
  // suivi environnemental : seul l'areaType de CETTE zone est réécrit, à partir
  // du doc — les autres modifications en cours du formulaire ne partent pas.
  // Zone pas encore enregistrée → rien à écrire (found: false), elle partira
  // avec l'enregistrement de la fiche.
  async _setEnclosureAreaType(enclosureId, areaId, areaType) {
    let db = initDb();
    const docRef = db.collection("enclosures").doc(enclosureId);
    try {
      let found = false;
      await db.runTransaction(async (transaction) => {
        const snapshot = await transaction.get(docRef);
        const areas = (snapshot.exists && snapshot.data().areas) || [];
        found = areas.some((area) => area?.areaId === areaId);
        if (!found) return;
        transaction.update(docRef, {
          areas: areas.map((area) => (area?.areaId === areaId ? { ...area, areaType } : area)),
        });
      });
      return { success: true, found };
    } catch (error) {
      console.error("[siteDatabaseConnector] _setEnclosureAreaType failed:", error);
      return { success: false, error: error };
    }
  }

  async _getSectorIndexFromZooId(zooId) {
    let db = initDb();
    let list = [];
    let snapshot = await db
      .collection("sectors")
      .where("zooId", "==", zooId)
      .get();
    snapshot.forEach((doc) => {
      const data = doc.data();
      list.push({
        label: data.sectorName,
        value: data.sectorId,
        ...data,
      });
    });
    return list;
  }

  async _getSectorFromId(sectorId) {
    let db = initDb();
    let data = await (
      await db.collection("sectors").doc(sectorId).get()
    ).data();

    return data;
  }
}
